import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../HireWithUs/Pages/HomePage/useSEO';
import Header from '../HireWithUs/Pages/Header&Footer/Header';
import Footer from '../HireWithUs/Pages/Header&Footer/Footer';
import { ArrowRight, Sparkles } from 'lucide-react';

/**
 * Case Studies listing. Uses the same header, footer, type, spacing,
 * and card treatment as the Resources/Blog page. Detail pages are not
 * part of this phase; each card only points at its future route.
 */
const CASE_STUDIES = [
  {
    slug: 'kinzy-club',
    name: 'Kinzy Club',
    category: 'CONSUMER / BRAND / GROWING COMPANY',
    story: 'From finding talent to finding the right people for the culture.',
    focus: 'Skills + attitude + culture',
    coverPosition: 'left center',
  },
  {
    slug: 'grand-gaming',
    name: 'Grand Gaming',
    category: 'GAMING / TECHNOLOGY',
    story:
      'When hiring volume spikes, recruitment has to scale without losing candidate experience.',
    focus: 'Surge hiring',
    coverPosition: 'center center',
  },
  {
    slug: 'manest-ai',
    name: 'manest.ai',
    category: 'AI / TECHNOLOGY / EARLY-STAGE',
    story:
      'How an early-stage AI team turned screening into an evidence-based technical hiring workflow.',
    focus: 'Technical hiring',
    coverPosition: 'right center',
  },
];

const CaseStudies = () => {
  useSEO({
    title: 'Case Studies — SearchJob.ai',
    description:
      'Customer case studies from Kinzy Club, Grand Gaming, and manest.ai.',
    canonical: 'https://searchjob.ai/case-studies',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 pt-16">
        <section className="relative isolate overflow-hidden bg-background">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute left-[8%] top-[-15%] h-[55%] w-[55%] rounded-full bg-primary/15 blur-3xl" />
            <div className="absolute right-[5%] top-[5%] h-[45%] w-[45%] rounded-full bg-emerald-500/10 blur-3xl" />
          </div>
          <div
            className="pointer-events-none absolute inset-0 -z-10 opacity-[0.12]"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              color: 'hsl(var(--foreground) / 0.1)',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
            }}
          />
          <div className="mx-auto max-w-3xl px-4 pb-14 pt-16 text-center sm:px-6 lg:px-8 lg:pt-20">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Resources
            </div>
            <h1 className="whitespace-nowrap text-4xl font-bold leading-[1.08] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-6xl">
              Case{' '}
              <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                studies.
              </span>
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              Customer stories from Kinzy Club, Grand Gaming, and manest.ai.
            </p>
          </div>
        </section>

        <section className="bg-background py-10 sm:py-12 lg:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-6">
              {CASE_STUDIES.map((study) => (
                <Link
                  key={study.slug}
                  to={`/case-studies/${study.slug}`}
                  aria-label={`Read the ${study.name} case study`}
                  className="group flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_50px_-16px_hsl(var(--primary)/0.28)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <div
                    className="relative aspect-[16/9] shrink-0 bg-primary/10"
                    style={{
                      backgroundImage: "url('/case-study-covers.png')",
                      backgroundPosition: study.coverPosition,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '300% 100%',
                    }}
                    aria-hidden="true"
                  >
                    <span className="absolute bottom-0 left-5 translate-y-1/2 rounded-full border border-primary/20 bg-background px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary shadow-sm">
                      {study.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col gap-3 p-6">
                    <h2 className="text-lg font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
                      {study.name}
                    </h2>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {study.story}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      <span className="font-semibold uppercase tracking-wider">Focus</span>
                      {' · '}
                      {study.focus}
                    </p>
                    <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold text-primary transition-all group-hover:gap-2">
                      Read case study
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CaseStudies;
